import React, { useState } from "react";

interface LabRequestFormProps {
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

const LabRequestForm: React.FC<LabRequestFormProps> = ({
  initialData,
  onSave,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    tests: initialData?.tests || [],
    status: initialData?.status || "pending",
  });
  const [newTest, setNewTest] = useState("");

  const linkedOrder = initialData?.labRequest ?? initialData?.lab_request ?? null;
  const isEditMode = !!(linkedOrder || initialData?.labOrderId || initialData?.lab_order_id);
  const [editNotes, setEditNotes] = useState<string>(initialData?.notes ?? linkedOrder?.notes ?? "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.tests.length === 0) {
      alert("Please add at least one test");
      return;
    }
    onSave(formData);
  };

  const addTest = () => {
    if (newTest.trim()) {
      setFormData({
        ...formData,
        tests: [...formData.tests, newTest.trim()],
      });
      setNewTest("");
    }
  };

  const removeTest = (index: number) => {
    setFormData({
      ...formData,
      tests: formData.tests.filter((_: string, i: number) => i !== index),
    });
  };

  if (isEditMode) {
    const orderId = linkedOrder?.id ?? initialData?.labOrderId ?? initialData?.lab_order_id;
    const test = linkedOrder?.labTest ?? linkedOrder?.lab_test;
    const statusColors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      in_progress: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      reported: "bg-green-100 text-green-800",
    };
    const status = linkedOrder?.status ?? "pending";

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ labOrderId: orderId, notes: editNotes });
        }}
        className="p-4 space-y-4"
      >
        {/* Lab order summary */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">
              Order: {linkedOrder?.orderNumber ?? linkedOrder?.order_number ?? `#${orderId ?? "—"}`}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusColors[status] ?? "bg-gray-100 text-gray-700"}`}>
              {status.replace(/_/g, " ")}
            </span>
          </div>
          {test && (
            <div>
              <span className="text-xs text-gray-500 uppercase font-medium">Test</span>
              <p className="text-sm font-medium text-gray-800 mt-0.5">
                {test.name}
                {test.code && <span className="text-gray-500 ml-1">({test.code})</span>}
              </p>
              {test.description && (
                <p className="text-xs text-gray-500 mt-0.5">{test.description}</p>
              )}
            </div>
          )}
          {(linkedOrder?.sampleId ?? linkedOrder?.sample_id) && (
            <p className="text-xs text-gray-600">
              Sample ID: <strong>{linkedOrder.sampleId ?? linkedOrder.sample_id}</strong>
            </p>
          )}
          {(linkedOrder?.priority) && (
            <p className="text-xs text-gray-600 capitalize">
              Priority: <strong>{linkedOrder.priority}</strong>
            </p>
          )}
          {(linkedOrder?.referredBy ?? linkedOrder?.referred_by) && (
            <p className="text-xs text-gray-600">
              Referred by: <strong>{linkedOrder.referredBy ?? linkedOrder.referred_by}</strong>
            </p>
          )}
        </div>

        {/* Editable notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            rows={3}
            placeholder="Add notes…"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700">
            Save
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">

      {/* ── Linked lab order (edit mode) ── */}
      {linkedOrder && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800">
              Linked Lab Order: {linkedOrder.orderNumber ?? linkedOrder.order_number ?? `#${linkedOrder.id}`}
            </span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              (linkedOrder.status === "completed" || linkedOrder.status === "reported")
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}>
              {linkedOrder.status ?? "pending"}
            </span>
          </div>
          {(linkedOrder.labTest ?? linkedOrder.lab_test) && (
            <p className="text-sm text-blue-900">
              Test: <strong>{(linkedOrder.labTest ?? linkedOrder.lab_test)?.name}</strong>
              {(linkedOrder.labTest ?? linkedOrder.lab_test)?.code && (
                <span className="text-blue-600 ml-1">({(linkedOrder.labTest ?? linkedOrder.lab_test)?.code})</span>
              )}
            </p>
          )}
          {linkedOrder.sampleId ?? linkedOrder.sample_id ? (
            <p className="text-xs text-blue-700">Sample ID: {linkedOrder.sampleId ?? linkedOrder.sample_id}</p>
          ) : null}
          {linkedOrder.notes && (
            <p className="text-xs text-blue-700">Notes: {linkedOrder.notes}</p>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Request Tests
        </label>
        <div className="flex gap-2 mt-1">
          <input
            type="text"
            value={newTest}
            onChange={(e) => setNewTest(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTest();
              }
            }}
            className="flex-1 block rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
            placeholder="Enter test name"
          />
          <button
            type="button"
            onClick={addTest}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Add
          </button>
        </div>
        {formData.tests.length > 0 && (
          <ul className="mt-2 space-y-1">
            {formData.tests.map((test: string, index: number) => (
              <li
                key={index}
                className="flex justify-between items-center p-2 bg-gray-50 rounded"
              >
                <span className="text-sm text-gray-700">{test}</span>
                <button
                  type="button"
                  onClick={() => removeTest(index)}
                  className="text-sm text-red-600 hover:text-red-900"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Status
        </label>
        <select
          value={formData.status}
          onChange={(e) =>
            setFormData({ ...formData, status: e.target.value })
          }
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
        >
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
        </select>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Save Request
        </button>
      </div>
    </form>
  );
};

export default LabRequestForm;

